<template>
  <VDialogV3
      id="payment-requisites-dialog"
      position="right"
      width="600px"
      :entity="localPaymentRequest"
      @submit="onSubmit"
      @close="onClose"
  >
    <section>
      <header>Основное</header>
      <VSelectV2
          v-model="localPaymentRequest.receiver"
          label="Продавец"
          layout="vertical"
          :data="receiverSource"
          required
          :disabled="isNewDocument === false"
      />

      <VSelectV2
          v-model="localPaymentRequest.receiverCompany"
          label="Юр лицо"
          layout="vertical"
          :disabled="isNewDocument === false"
          hide-clear-btn
          :data="receiverCompanySource"
          required
      />

      <VSelectV2
          v-model="localPaymentRequest.receiverBankAccount"
          label="Расчётный счёт"
          layout="vertical"
          :data="receiverBankAccountSource"
          :required="localPaymentRequest.isForeign === false"
      >
        <template #option="{ option }">
          {{ option.toString() }}
          <div
              v-if="option.isMain === true"
              class="description"
          >
            Основной
          </div>
        </template>
      </VSelectV2>
    </section>

    <section>
      <VSelectV2
          v-model="localPaymentRequest.reasonType"
          label="Основание платежа"
          layout="vertical"
          :data="reasonTypeSource"
          hide-clear-btn
          required
          :disabled="isNewDocument === false"
      />

      <VSelectV2
          v-if="isOrderPositionReasonType === true"
          key="positionInput"
          :model-value="localPaymentRequest.orderPosition"
          @update:modelValue="onOrderPositionSelected"
          :data="orderPositionsSource"
          empty-data-text="Нет неоплаченных позиций"
          label="Позиция"
          layout="vertical"
          hide-clear-btn
          required
      />

      <VSelectV2
          v-if="isShipmentReasonType === true"
          key="shipmentInput"
          :model-value="localPaymentRequest.shipment"
          @update:modelValue="onShipmentSelected"
          label="Отгрузка"
          :data="shipmentsSource"
          empty-data-text="Нет неоплаченных отгрузок"
          layout="vertical"
          hide-clear-btn
          required
      />

      <!-- Фильтр по позиции, с которой начали: список сужен, и об этом надо
           сказать, иначе отсутствие нужного документа выглядит как сбой. -->
      <div
          v-if="basisOrderPosition !== null"
          class="description"
      >
        Показаны только документы с позицией №{{ $core.utils.string.formatWithDelimiter(basisOrderPosition.id) }}
      </div>

      <VSelectV2
          v-if="isOrderReasonType === true"
          key="orderInput"
          :model-value="localPaymentRequest.order"
          @update:modelValue="onOrderSelected"
          label="Заказ"
          :data="ordersSource"
          empty-data-text="Нет неоплаченных заказов"
          layout="vertical"
          hide-clear-btn
          required
      />

      <!-- Фильтр по позиции, с которой начали: список сужен, и об этом надо
           сказать, иначе отсутствие нужного документа выглядит как сбой. -->
      <div
          v-if="basisOrderPosition !== null"
          class="description"
      >
        Показаны только документы с позицией №{{ $core.utils.string.formatWithDelimiter(basisOrderPosition.id) }}
      </div>

      <!-- Мультивалютный пикер при заказе в нескольких валютах -->
      <div
          v-if="showOrderCurrencySelect === true"
          class="order-currency"
      >
        <div
            v-for="(currency, key) in Array.from(localPaymentRequest.order.currencies)"
            :key="key"
            class="order-currency-item"
            :class="{
						selected: localPaymentRequest.orderCurrency?.code === currency[0],
					}"
            @click="onOrderPositionCurrencyInput(currency[0])"
        >
          <header>Оплатить {{ currency[0] }}</header>

          <div
              v-for="position in currency[1]"
              :key="position.id"
              class="order-position"
          >
            <div
                class="name ellipsis"
                :title="position.name"
            >
              {{ position.name }}
            </div>
            <div class="sum">
              {{ position.sumFullSeller.format(currency[0]) }}
            </div>
          </div>
        </div>
      </div>

      <VSelectV2
          v-model="localPaymentRequest.orderCurrency"
          label="Валюта сделки"
          layout="vertical"
          :data="currencySource"
          hide-clear-btn
          :disabled="orderCurrencyDisabled"
      >
        <template #option="{ option }">
          <div>{{ option.description }}</div>
          <div class="description">
            {{ option.code }}
          </div>
        </template>
      </VSelectV2>

      <template v-if="invoiceControlsVisible === true">
        <VInput
            key="invoiceNumberInput"
            v-model="localPaymentRequest.invoice.number"
            label="Номер счёта"
            layout="vertical"
            required
            @update:modelValue="updateReasonText"
        />

        <VDateInput
            key="invoiceDateInput"
            v-model="localPaymentRequest.invoice.date"
            label="Дата счёта"
            layout="vertical"
            required
            @update:modelValue="updateReasonText"
        />

        <VSelectV2
            key="invoiceCurrencyInput"
            v-model="localPaymentRequest.invoice.currency"
            label="Валюта счёта"
            layout="vertical"
            :data="currencySource"
            hide-clear-btn
            :disabled="invoiceCurrencyDisabled === true || isNewDocument === false"
            required
            @update:modelValue="onInvoiceCurrencyInput"
        >
          <template #option="{ option }">
            <div>{{ option.description }}</div>
            <div class="description">
              {{ option.code }}
            </div>
          </template>
        </VSelectV2>

      </template>

      <!-- Тип оплаты и курс — только когда валюта сделки не локальная,
           иначе конвертировать нечего.

           Стоят здесь, а не в блоке платежа: тип выводится из валюты сделки
           и валюты счёта, то есть из полей выше. Стоя раньше них, он
           показывал промежуточное значение и переставлялся сам уже после
           того, как форма наполовину заполнена. -->
      <template v-if="showCourseControls === true">
        <VCheckboxGroup
            v-model="localPaymentRequest.paymentType"
            label="Тип оплаты"
            layout="vertical"
            type-radio
            required
            :options="paymentTypeOptions"
            option-label="name"
            :disabled="isNewDocument === false"
        />

        <!-- Курс только показывается, и только когда он уже посчитан. Задаёт
             его сумма, введённая в позицию платежа: в счёте написана сумма, а
             не курс, и считать курс в уме пользователю незачем.

             Пустое поле здесь показывать нечего и нечем заполнить — позиции
             лежат в карточке, за этим диалогом. Поэтому пока курса нет, поля
             нет, а что делать, говорит сама карточка причиной
             reasonUnavailableSubmit, прямо под гридом. -->
        <VInputNumberRusV2
            v-if="localPaymentRequest.paymentType?.id === 'byCustomRate' && localPaymentRequest.hasCurrencyRate === true"
            key="customCurrencyRateInput"
            :type="CurrencyRate"
            label="Курс"
            layout="vertical"
            disabled
            :model-value="localPaymentRequest.paymentRequestCurrencyRate"
        />
      </template>
    </section>

    <section>
      <template v-if="userCanCreateManual === true">
        <VSwitch
            v-model="localPaymentRequest.isManual"
            theme="slider"
            layout="vertical"
            checked-label="Регистрация внешнего платежа"
            unchecked-label="Обычный платёж"
        />
      </template>

      <VInput
          v-if="localPaymentRequest.isManual === true"
          v-model="localPaymentRequest.externalNumber"
          label="Внешний номер платежа"
          layout="vertical"
          required
          :disabled="localPaymentRequest.isManual !== true && userCanCreateManual === false"
      />
    </section>

    <hr>

    <section>
      <header>Платёж</header>
      <VInput
          label="Валюта платежа"
          layout="vertical"
          disabled
          :value="localPaymentRequest.actualPaymentCurrency?.description"
      />

      <VDateInput
          v-model="localPaymentRequest.plannedPaymentDate"
          label="Дата оплаты"
          layout="vertical"
          required
          :validate-method="validatePaymentDate"
      >
        <template #helper-text>
          <div
              v-if="paymentDays.size > 0"
              class="date-list"
          >
            <span class="label">Плановые даты оплат: </span>
            <div
                class="ellipsis"
                :title="paymentDaysString"
            >
							<span
                  v-for="(date, index) in paymentDays.values()"
                  class="ellipsis"
                  :title="$core.utils.date.format(date) ?? ''"
                  :key="index"
              >{{ $core.utils.date.format(date) }} </span>
            </div>
          </div>
        </template>
      </VDateInput>

      <!-- Процент оплаты - операция ввода, а не поле документа: он
           раскладывается по количествам позиций и больше нигде не живёт.
           Показываемое значение выводится из снимка основания, поэтому
           переживает и сохранение, и редактирование заказа. -->
      <VInputNumberRus
          v-if="localPaymentRequest.paymentType?.id !== 'byDocumentExactly'"
          :value="localPaymentRequest.paymentSharePercent"
          label="Процент оплаты"
          layout="vertical"
          :max="100"
          @input="value => localPaymentRequest.applyPaymentPercent(value)"
      />

      <VInputNumberRusV2
          key="paymentRequestSumFull"
          v-model="localPaymentRequest.paymentRequestSumFull"
          :type="Sum"
          label="Итоговая сумма"
          layout="vertical"
          disabled
          required
      />

      <VInputNumberRusV2
          key="paymentRequestVatSum"
          v-model="localPaymentRequest.paymentRequestVatSum"
          :type="Sum"
          label="Сумма НДС"
          layout="vertical"
          disabled
          required
      />
    </section>

    <hr>

    <section>
      <!-- Обязательное и при редактировании. Раньше стояло :required на
           создании, но бэк требует основание всегда: продуктовая категория
           позиции может быть с ним связана, и пустое основание валидацию не
           проходит. Старый платёж без основания всё равно не сохранить,
           так что честнее сказать об этом сразу, а не на сабмите. -->
      <VSelectV2
          v-model="localPaymentRequest.reasonBasis"
          label="Что оплачивается"
          layout="vertical"
          required
          :data="$core.dictionaries.paymentReasonBases"
          @update:modelValue="updateReasonText"
      />

      <!-- Дата плана производства — фишка ЛСЗ: остальным партнёрам поле не
           заполняется автоматически и не нужно. Тем же условием оно
           инициализируется в PaymentRequest.initManufactureDate. -->
      <VDateCustomFormatInput
          v-if="$core.currentPartner?.isLSZ === true"
          mode="month"
          :value="localPaymentRequest.manufactureDate"
          label="Дата плана производства"
          layout="vertical"
          required
          :disabled="localPaymentRequest.isPaid"
          @input="value => localPaymentRequest.manufactureDate = value"
      >
        <template #helper-text>
          Полная дата: {{ productionPlanDate ?? '--' }}
        </template>
      </VDateCustomFormatInput>

      <VSelectV2
          v-model="localPaymentRequest.productionSite"
          label="Производственная площадка"
          layout="vertical"
          disabled
      />

      <!-- ЦФО — предустанавливается из площадки, можно переопределить. Только Наманган -->
      <VSelectV2
          v-if="$core.currentPartner?.isNamangan === true"
          v-model="localPaymentRequest.swCostscenter"
          label="ЦФО"
          layout="vertical"
          :data="swCostscentersSource"
      />

      <!-- Назначение платежа Oracle — загружается по reasonBasis + productionSite -->
      <VSelectV2
          v-model="localPaymentRequest.swPaymentDestination"
          label="Назначение платежа"
          layout="vertical"
          :data="swPaymentDestinationsSource"
      />

      <!-- Статья бюджета и текст назначения — только Наманган -->
      <template v-if="$core.currentPartner?.isNamangan === true">
        <!-- Статья бюджета — загружается по swPaymentDestination -->
        <VSelectV2
            v-model="localPaymentRequest.swBudgetItem"
            label="Статья бюджета"
            layout="vertical"
            :data="swBudgetItemsSource"
        >
          <template #option="{ option }">
            <div>{{ option.name }}</div>
            <div class="budget-item-code">{{ option.code }}</div>
          </template>
        </VSelectV2>

        <VTextarea
            v-model="localPaymentRequest.reasonText"
            label="Назначение платежа"
            layout="vertical"
            disabled
        />
      </template>
    </section>

    <hr>

    <section>
      <VSelectV2
          v-model="localPaymentRequest.priorityType"
          layout="vertical"
          label="Приоритет платежа"
          :data="$core.dictionaries.paymentPriorityTypes"
      />

      <VTextarea
          v-model="localPaymentRequest.commentary"
          label="Комментарий"
          layout="vertical"
      />
    </section>

    <hr>

    <section v-if="typeof localPaymentRequest.isOfferBased === 'boolean'">
      <VSwitch
          v-model="localPaymentRequest.isOfferBased"
          theme="slider"
          disabled
          checked-label="Создан на основе предложения"
          unchecked-label="Создан не на основе предложения"
          layout="vertical"
      />
    </section>

    <hr>

    <section>
      <VSelectV2
          v-model="localPaymentRequest.responsible"
          layout="vertical"
          label="Ответственный"
          placeholder="Введите ФИО ответственного"
          hide-clear-btn
          :data="responsibleSource"
          required
      />
    </section>
  </VDialogV3>
</template>

<script lang="ts">
import DateSimple                   from '@/core/types/DateSimple';

import PaymentRequest               from '@/domains/PaymentRequests/PaymentRequest';
import type Shipment               from '@/domains/Shipments/Shipment';
import type Order                  from '@/domains/Orders/Order';
import type Partner                from '@/domains/Partners/Partner';
import type Company                from '@/domains/Companies/Company';
import type OrderPosition          from '@/domains/Orders/OrderPosition';
import PaymentRequestsService       from '@/domains/PaymentRequests/PaymentRequestsService';
import PaymentType                  from '@/domains/PaymentRequests/PaymentType';

import SWCostscenter                from '@/domains/SWCostscenters/SWCostscenter';
import SWPaymentDestinationsService from '@/domains/SWPaymentDestinations/SWPaymentDestinationsService';
import SWBudgetItemsService         from '@/domains/SWBudgetItems/SWBudgetItemsService';

import PartnersService              from '@/domains/Partners/PartnersService';
import ShipmentsService             from '@/domains/Shipments/ShipmentsService';
import OrdersService                from '@/domains/Orders/OrdersService';
import PersonsService               from '@/domains/Persons/PersonsService';
import CompaniesService             from '@/domains/Companies/CompaniesService';
import ProductionSitesService       from '@/domains/ProductionSites/ProductionSitesService';
import Sum                          from "@/core/types/Sum";
import CurrencyRate                 from "@/core/types/CurrencyRate";

export default defineComponent({
  name: 'PaymentRequestRequisites',

  props: {
    paymentRequest: {
      type: PaymentRequest,
      required: true,
    },
  },

  emits: ['save', 'close'],

  setup() {
    return {
      Sum,
      CurrencyRate,
      PaymentRequestsService,
    }
  },

  data() {
    const localPaymentRequest = this.$core.utils.cloneDeep(this.paymentRequest);
    const isNewDocument       = this.$core.utils.isNullOrUndefined(localPaymentRequest.id);

    return {
      isNewDocument,
      localPaymentRequest,

      /**
       * Позиция заказа, с которой начали оформление платежа.
       *
       * Живёт в компоненте, а не в модели: платежу она не нужна, как только
       * основанием стал другой документ, а форме нужна — ей она сужает
       * списки отгрузок и заказов до тех, что эту позицию содержат. Модель
       * свою ссылку на позицию при смене типа основания обнуляет, поэтому
       * помнить приходится здесь.
       */
      basisOrderPosition: localPaymentRequest.orderPosition ?? null,

      receiverSource: this.$core.loader.select(({ pattern }) => PartnersService.searchSellers(pattern)),

      receiverCompanySource: this.$core.loader.select(({ receiver }) => CompaniesService.getList({ partnerId: receiver.id }))
          .dependsOn({ receiver: () => this.localPaymentRequest.receiver })
          .preloadOnMount(isNewDocument === true)
          .preloadOnFocus(isNewDocument === false),

      receiverBankAccountSource: this.$core.loader.select(({ receiverCompany }) => receiverCompany.bankAccounts ?? [])
          .dependsOn({ receiverCompany: () => this.localPaymentRequest.receiverCompany })
          .preloadOnMount(isNewDocument === true)
          .preloadOnFocus(isNewDocument === false),

      reasonTypeSource: this.$core.loader.select(({ pattern }) => {
        const reasonTypes = PaymentRequestsService.reasonTypes;
        if (!pattern) return reasonTypes;

        return reasonTypes.filter(item => {
          return item.name.toLowerCase().includes(pattern.toLowerCase());
        });
      }).preloadOnMount(),

      shipmentsSource: this.$core.loader.select(({ receiver, receiverCompany, basisOrderPosition }) => ShipmentsService.getList({
        seller:            { id: receiver.id },
        sellerCompany:     { id: receiverCompany.id },
        woPaymentRequests: true,
        idPattern:         basisOrderPosition?.id,
      }))
          .dependsOn({
            receiver:        () => this.localPaymentRequest.receiver,
            receiverCompany: () => this.localPaymentRequest.receiverCompany,
          })
          .dependsOnNullable({ basisOrderPosition: () => this.basisOrderPosition })
          .preloadOnMount(isNewDocument === true)
          .preloadOnFocus(isNewDocument === false),

      ordersSource: this.$core.loader.select(({ receiver, receiverCompany, basisOrderPosition }) => OrdersService.getOrdersList({
        seller:            { id: receiver.id },
        sellerCompany:     { id: receiverCompany.id },
        woPaymentRequests: true,
        idPattern:         basisOrderPosition?.id,
      }))
          .dependsOn({
            receiver:        () => this.localPaymentRequest.receiver,
            receiverCompany: () => this.localPaymentRequest.receiverCompany,
          })
          .dependsOnNullable({ basisOrderPosition: () => this.basisOrderPosition })
          .preloadOnMount(isNewDocument === true)
          .preloadOnFocus(isNewDocument === false),

      orderPositionsSource: this.$core.loader.select(({ receiver, receiverCompany }) => OrdersService.getPositionsList({
        seller:         { id: receiver.id },
        sellerCompany:  { id: receiverCompany.id },
        paymentStatus:  'not-paid',
        deliveryStatus: 'all',
      }))
          .dependsOn({
            receiver:        () => this.localPaymentRequest.receiver,
            receiverCompany: () => this.localPaymentRequest.receiverCompany,
          })
          .preloadOnMount(isNewDocument === true)
          .preloadOnFocus(isNewDocument === false),

      /**
       * Валюты, допустимые для сделки/счёта, — считает модель
       * (availableCurrencies). Источник общий для селектов «Валюта
       * сделки» и «Валюта счёта». Зависимости — ровно то, что читает
       * геттер: получатель (его локальная валюта) и код валюты сделки
       * (примитив, dependencyKey переварит без id).
       */
      currencySource: this.$core.loader.select(() => this.localPaymentRequest.availableCurrencies)
          .dependsOn({
            receiver: () => this.localPaymentRequest.receiver,
          })
          /** Валюта сделки нужна: availableCurrencies от неё зависит, и без
           *  перечитывания список остаётся тем, каким был на момент загрузки.
           *  Обязательной её сделать нельзя — пока она не выбрана, источник
           *  перестал бы грузиться вовсе. */
          .dependsOnNullable({
            orderCurrencyCode: () => this.localPaymentRequest.orderCurrencyCode,
          })
          .preloadOnMount(isNewDocument === true)
          .preloadOnFocus(isNewDocument === false),

      /**
       * ЦФО: вилки прежнего loadSwCostscenters (дозагрузка неполной
       * площадки, samples-список) живут в loadSwCostscenterOptions.
       */
      swCostscentersSource: this.$core.loader.select(({ productionSite }) => this.loadSwCostscenterOptions(productionSite))
          .dependsOn({ productionSite: () => this.localPaymentRequest.productionSite })
          .preloadOnMount(isNewDocument === true)
          .preloadOnFocus(isNewDocument === false),

      swPaymentDestinationsSource: this.$core.loader.select(
          ({ reasonBasis, productionSite }) => SWPaymentDestinationsService.getList({ paymentReasonBasis: reasonBasis, productionSite }),
      )
          .dependsOn({
            reasonBasis:    () => this.localPaymentRequest.reasonBasis,
            productionSite: () => this.localPaymentRequest.productionSite,
          })
          .preloadOnMount(isNewDocument === true)
          .preloadOnFocus(isNewDocument === false),

      swBudgetItemsSource: this.$core.loader.select(({ swPaymentDestination }) => SWBudgetItemsService.getList({ swPaymentDestination }))
          .dependsOn({ swPaymentDestination: () => this.localPaymentRequest.swPaymentDestination })
          .preloadOnMount(isNewDocument === true)
          .preloadOnFocus(isNewDocument === false),

      responsibleSource: this.$core.loader.select(
          ({ pattern }) => PersonsService.searchPersons(pattern, this.$core.currentPartner.id),
      ),
    };
  },

  computed: {
    hasReasonType(): boolean {
      return this.localPaymentRequest?.reasonType?.id !== undefined
          && this.localPaymentRequest.reasonType.id !== null;
    },

    // ==================== Типы оснований ====================

    isShipmentReasonType(): boolean {
      if (!this.hasReasonType) return false;
      return this.localPaymentRequest.reasonType.requiresShipment;
    },

    isOrderReasonType(): boolean {
      if (!this.hasReasonType) return false;
      return this.localPaymentRequest.reasonType.requiresOrder;
    },

    isOrderPositionReasonType(): boolean {
      if (!this.hasReasonType) return false;
      return this.localPaymentRequest.reasonType.requiresOrderPosition;
    },

    /** Основание на базе заказа (order или invoiceOrder) */
    basedOrder(): boolean {
      return this.hasReasonType
          && ['order', 'invoiceOrder'].includes(this.localPaymentRequest.reasonType.id);
    },

    /** Основание на базе standalone-инвойса */
    basedInvoice(): boolean {
      return this.hasReasonType
          && this.localPaymentRequest.reasonType.isStandaloneInvoice;
    },

    /** Показывать контролы инвойса */
    invoiceControlsVisible(): boolean {
      return this.hasReasonType
          && this.localPaymentRequest.reasonType.requiresInvoice;
    },

    // ==================== Мультивалютность ====================

    orderCurrencyList(): string[] {
      return this.basedOrder && this.localPaymentRequest.order
          ? this.localPaymentRequest.order.currencyList
          : [];
    },

    showOrderCurrencySelect(): boolean {
      return this.orderCurrencyList?.length > 1;
    },

    /** Валюту сделки можно выбрать только у самостоятельного счёта в
     *  локальной валюте — во всех прочих случаях она берётся из основания.
     *
     *  Сравнение шло по id валюты со строкой 'RUB', а в id лежит числовой
     *  код (643 у рубля), буквенный — в code. Условие было ложно всегда, и
     *  поле стояло недоступным при любом основании. Заодно ушёл литерал:
     *  локальная валюта у Намангана не рубль. */
    orderCurrencyDisabled(): boolean {
      return !(
          this.basedInvoice
          && this.localPaymentRequest.invoice?.currency
          && this.localPaymentRequest.invoice.currency?.code === this.localPaymentRequest.actualPaymentCurrencyCode
      );
    },

    invoiceCurrencyDisabled(): boolean {
      if (!this.hasReasonType) return false;
      switch (this.localPaymentRequest.reasonType.id) {
        case 'invoiceShipment':
          if (!this.localPaymentRequest.shipment) return false;
          return this.localPaymentRequest.shipment.currencyCode === this.localPaymentRequest.actualPaymentCurrencyCode;
        case 'invoiceOrder':
          if (!this.localPaymentRequest.order) return false;
          return this.localPaymentRequest.order.currencyCode === this.localPaymentRequest.actualPaymentCurrencyCode;
        default:
          return false;
      }
    },

    // ==================== Тип оплаты ====================

    /**
     * Доступные типы оплаты. Правила живут в модели: они относятся к платежу,
     * а не к форме, и по ним же надо проверять на сохранении. Здесь была
     * вторая, слегка расходившаяся копия тех же условий.
     */
    paymentTypeOptions(): PaymentType[] {
      return this.localPaymentRequest.getAvailablePaymentTypes();
    },

    showCourseControls(): boolean {
      /** Если основание — счёт поставщика и валюта счёта локальная — прячем */
      if (
          this.localPaymentRequest.reasonType?.id === 'invoice'
          && this.localPaymentRequest?.invoice?.currency?.code === this.localPaymentRequest.actualPaymentCurrencyCode
      ) {
        return false;
      }
      return this.localPaymentRequest?.orderCurrency?.code !== this.localPaymentRequest.actualPaymentCurrencyCode;
    },

    // customCurrencyRateDisabled удалён: поле показывается только при типе
    // «произвольный курс», а он и означает, что курс вводит пользователь.
    // Условие разрешало ввод лишь при основании invoiceShipment, из-за чего
    // на остальных основаниях тип выбирался, а курс оставался прежним.
    // Ограничивать надо доступность самого типа в getAvailablePaymentTypes,
    // а не редактируемость поля под ним.

    // ==================== Ручной платёж ====================

    /** Право регистрировать внешний платёж — хардкод-список id в Person */
    userCanCreateManual(): boolean {
      return this.$core.currentUser?.canEditPaymentExternalId === true;
    },

    // ==================== Плановые даты оплат ====================

    /**
     * Плановые даты оплат — из позиций самого платежа, а не из документа
     * основания. У позиции отгрузки plannedPaymentDate пустая (она там
     * эмулирована), поэтому для платежа по отгрузке список выходил пустым
     * и подсказка под датой оплаты не показывалась вовсе. У позиции заказа
     * дата есть всегда.
     *
     * Заодно уходит ветвление по типу основания: позиции платежа собраны
     * к этому моменту при любом из них.
     */
    paymentDays(): Map<number, any> {
      const paymentDays = new Map<number, any>();

      for (const position of this.localPaymentRequest.positions) {
        const plannedPaymentDate = position.orderPosition?.plannedPaymentDate;

        if (plannedPaymentDate !== null && plannedPaymentDate !== undefined) {
          if (paymentDays.has(+plannedPaymentDate) === false) {
            paymentDays.set(+plannedPaymentDate, plannedPaymentDate);
          }
        }
      }

      return paymentDays;
    },

    paymentDaysString(): string | null {
      if (this.paymentDays.size === 0) return null;
      return Array.from(this.paymentDays.values())
          .reduce((acc, date) => acc + `${this.$core.utils.date.format(date)} `, '');
    },

    productionPlanDate(): string | null {
      return this.$core.utils.date.format(this.localPaymentRequest.manufactureDate);
    },
  },

  methods: {
    // ==================== Закрытие / сабмит ====================

    onClose() {
      this.$emit('close');
    },

    /**
     * Реагируем на сабмит. Валидацию формы уже выполнил VDialogV3 —
     * сюда долетаем только когда форма валидна. Наше дело — отдать
     * локальный объект наверх и закрыть диалог.
     */
    onSubmit(): void {
      this.$emit('save', this.localPaymentRequest);
      this.$core.dialogs.PaymentRequestRequisites.hide();
    },

    // ==================== Загрузка данных ====================

    /**
     * Список ЦФО для площадки. Вилки прежнего loadSwCostscenters:
     * - площадка неполная (огрызок {id, name} из отгрузки, без
     *   swCostscenter) — дозагружаем полный объект и подменяем его
     *   в платеже. Возвращаем список сразу по полному объекту:
     *   dependencyKey строится по id, подмена объекта с тем же id
     *   повторную загрузку не вызовет;
     * - площадка образцов — захардкоженный список;
     * - иначе — единственный ЦФО площадки (автовыбор сделает селект).
     */
    async loadSwCostscenterOptions(site: any): Promise<SWCostscenter[]> {
      if (!site.swCostscenter && site.id !== 'sw_berdsk_research') {
        const fullSite = await ProductionSitesService.get(site.id);
        if (fullSite) {
          this.localPaymentRequest.productionSite = fullSite;
          site = fullSite;
        }
      }

      if (site.id === 'sw_berdsk_research') {
        return SWCostscenter.getSamplesPaymentList();
      } else if (site.swCostscenter) {
        return [site.swCostscenter];
      } else {
        return [];
      }
    },

    // ==================== Основание ====================

    /**
     * Выбрали отгрузку — собираем платёж заново фабрикой. Старый объект
     * не донастраиваем: в нём остаются позиции и производные поля от
     * прежнего основания.
     */
    onShipmentSelected(shipment: Shipment) {
      this.localPaymentRequest = PaymentRequest.fromShipment(shipment, {
        receiver:        this.localPaymentRequest.receiver as Partner,
        receiverCompany: this.localPaymentRequest.receiverCompany as Company,
        /** Тип основания выбран пользователем и пересборку переживает:
         *  без него фабрика ставила «Отгрузку» поверх «Счёта поставщика
         *  по отгрузке», и реквизиты счёта исчезали с формы. */
        reasonType:      this.localPaymentRequest.reasonType,
      });
    },

    /**
     * Выбрали заказ — то же, см. onShipmentSelected.
     *
     * Мультивалютный заказ основанием пока не берётся. Пустой код валюты
     * у существующего заказа как раз и означает несколько валют, а выбрать
     * одну негде: список заказов приходит без позиций, и поля с валютами
     * на заказе ещё нет. Без валюты сделки платёж не сохранится, поэтому
     * говорим об этом сразу, а не ловим ругань бэка про курс.
     */
    onOrderSelected(order: Order) {
      if (this.$core.utils.isNullOrUndefined(order.currencyCode) === true) {
        this.$core.error('Заказ в нескольких валютах — выбрать его основанием платежа пока нельзя');
      }
      else {
        this.localPaymentRequest = PaymentRequest.fromOrder(order, {
          receiver:        this.localPaymentRequest.receiver as Partner,
          receiverCompany: this.localPaymentRequest.receiverCompany as Company,
          reasonType:      this.localPaymentRequest.reasonType,
        });
      }
    },

    /**
     * Выбрали позицию заказа — то же, см. onShipmentSelected.
     *
     * Позицию запоминаем отдельно от модели: она переживёт смену основания
     * и сузит списки отгрузок и заказов через dependsOnNullable.
     */
    onOrderPositionSelected(orderPosition: OrderPosition) {
      this.basisOrderPosition = orderPosition;

      this.localPaymentRequest = PaymentRequest.fromOrderPosition(orderPosition, {
        receiver:        this.localPaymentRequest.receiver as Partner,
        receiverCompany: this.localPaymentRequest.receiverCompany as Company,
        reasonType:      this.localPaymentRequest.reasonType,
      });
    },

    // ==================== Валюта и тип оплаты ====================

    /**
     * Выбрали валюту в мультивалютном заказе.
     *
     * Отбор позиций, тип оплаты и курс здесь больше не задаются: всё это
     * делает каскад модели. Сеттер orderCurrencyCode ставит тип оплаты той
     * же развилкой (локальная валюта — прямой, иначе по курсу ЦБ), тип
     * ставит курс и зовёт rebuildPositions, а та отбирает позиции заказа
     * в выбранной валюте.
     *
     * Прежний код делал то же самое вручную и расходился с моделью: отбор
     * позиций работал только в рублёвой ветке, а в валютной в платёж
     * попадали позиции всех валют заказа.
     */
    onOrderPositionCurrencyInput(currencyCode: string) {
      const currency = this.$core.utils.money.getCurrencyByCode(currencyCode);

      this.localPaymentRequest.order.currencyCode = currencyCode;

      if (this.localPaymentRequest.reasonType.id.startsWith('invoice')) {
        this.onInvoiceCurrencyInput(currency);
      }

      this.localPaymentRequest.orderCurrency = currency;
    },

    onInvoiceCurrencyInput(currency: any) {
      const localInvoice        = this.localPaymentRequest.invoice;

      localInvoice.currency     = currency;
      localInvoice.currencyCode = currency?.code ?? null;

      this.localPaymentRequest.invoice = { ...localInvoice };

      /**
       * Логика выбора типа оплаты при смене валюты счёта:
       * - Валюта сделки неизвестна → по курсу ЦБ
       * - Валюты сделки и счёта совпадают → текущий тип оплаты
       * - Разошлись → произвольный курс: платим в валюте счёта, а курс
       *   задаёт пользователь — суммой из счёта либо напрямую
       */
      if (!this.localPaymentRequest.orderCurrencyCode) {
        this.localPaymentRequest.paymentType = PaymentType.BY_RATE_OF_CENTRAL_BANK;
      }
      else if (this.localPaymentRequest.orderCurrencyCode === currency?.code) {
        // Валюты совпадают — оставляем текущий тип
      }
      else {
        this.localPaymentRequest.paymentType = PaymentType.BY_CUSTOM_RATE;
      }
    },

    // ==================== Прочее ====================

    updateReasonText() {
      this.localPaymentRequest.updateReasonText();
    },

    // ==================== Валидация даты ====================

    validatePaymentDate(value: any): string | null {
      if (this.isNewDocument === false || this.localPaymentRequest.isManual) return null;
      if (!(value instanceof Date)) return null;

      const today = DateSimple.today();
      const paymentDate = new DateSimple(value);

      if (paymentDate.isBefore(today)) {
        return 'Нельзя указать прошедшую дату';
      }

      if (paymentDate.equals(today)) {
        return 'Нельзя указать сегодняшнюю дату оплаты';
      }

      const availableDays = [1, 4]; // Понедельник, четверг
      if (!availableDays.includes(value.getDay())) {
        const nextAvailableDate = this.getNextAvailableDate(value, availableDays);
        return `Допустимые дни оплаты: понедельник и четверг; Следующая дата ${this.$core.utils.date.format(nextAvailableDate)}`;
      }

      return null;
    },

    getNextAvailableDate(value: Date, availableDays: number[]): Date | null {
      const dayOfWeek = this.$core.utils.date.cloneDate(value).getDay();

      let daysUntilNextAvailable = Infinity;
      let nextAvailableDate: Date | null = null;

      availableDays.forEach(day => {
        if (day < 0 || day > 6) return;

        let daysUntil = (day - dayOfWeek + 7) % 7;
        if (daysUntil === 0) daysUntil = 7;

        if (daysUntil < daysUntilNextAvailable) {
          daysUntilNextAvailable = daysUntil;
          nextAvailableDate = new Date(value);
          nextAvailableDate!.setDate(value.getDate() + daysUntil);
        }
      });

      return nextAvailableDate;
    },

  },
});
</script>